import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/stores/auth';
import type { Role } from './roles';
import { appSchool, isNativeApp } from '@/lib/native';
import { AppSchoolHeader } from '@/components/AppSchoolHeader';

export interface SchoolChoice { id: number; name: string; code: string; city?: string | null; state?: string | null }

const KEY = 'ojp-school';
export const getChosenSchool = (): SchoolChoice | null => {
  // a school's own app is for that school only: its code goes with every sign-in, code and password reset
  if (appSchool) return { id: 0, name: appSchool.name, code: appSchool.code };
  try { return JSON.parse(sessionStorage.getItem(KEY) ?? 'null'); } catch { return null; }
};
export const setChosenSchool = (s: SchoolChoice | null) => (s ? sessionStorage.setItem(KEY, JSON.stringify(s)) : sessionStorage.removeItem(KEY));

export const inputCls = 'border rounded-xl w-full px-3 py-2 text-sm';

export function errText(e: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const r = (e as { response?: { data?: { message?: string; errors?: Record<string, string[]> } }; request?: unknown });
  const first = r.response?.data?.errors ? Object.values(r.response.data.errors)[0]?.[0] : undefined;
  if (first ?? r.response?.data?.message) return (first ?? r.response?.data?.message) as string;
  if (r.request && !r.response) {
    // say which address failed and how, so a phone problem can be traced
    const x = e as { code?: string; message?: string; config?: { baseURL?: string; url?: string } };
    const where = x.config ? `${x.config.baseURL ?? ''}${x.config.url ?? ''}` : '';
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    return offline ? 'You are offline. Check your data or Wi-Fi and try again.'
      : `Cannot reach the server. Check your connection.${x.code || x.message ? ` (${[x.code, x.message].filter(Boolean).join(': ')}${where ? ` · ${where}` : ''})` : ''}`;
  }
  return fallback;
}

export function deviceInfo() {
  let uuid = localStorage.getItem('ojp-device');
  if (!uuid) { uuid = crypto.randomUUID(); localStorage.setItem('ojp-device', uuid); }
  const ua = navigator.userAgent;
  // the phone app: "<School> app · Android 14"; the website: the browser's description (the server keeps 120 characters)
  const android = /Android [\d.]+/.exec(ua)?.[0] ?? 'Android';
  const name = isNativeApp ? `${appSchool?.name ?? 'OJP'} app · ${android}` : ua;
  return { uuid, platform: isNativeApp ? 'android' : 'web', name: name.slice(0, 120) };
}

interface SessionPayload {
  token: string;
  user: { id: number; name: string; role: Role; school_id: number | null; phone?: string | null };
  school?: { name?: string; code?: string } | null;
  flags?: { must_change_password?: boolean; phone_verified?: boolean } | null;
}

/** Persist the session returned by login / OTP / register. */
export function startSession(p: SessionPayload) {
  useAuth.getState().setSession(
    { id: p.user.id, name: p.user.name, role: p.user.role, schoolId: p.user.school_id, schoolName: p.school?.name, schoolCode: p.school?.code, phone: p.user.phone ?? null, mustChangePassword: !!p.flags?.must_change_password, phoneVerified: p.flags?.phone_verified ?? true },
    p.token,
  );
}

export function AuthShell({ title, subtitle, children, footer }: { title: string; subtitle?: string; children: ReactNode; footer?: ReactNode }) {
  return (
    <main className="min-h-screen grid place-items-center p-4">
      <div className="bg-white rounded-card shadow p-8 w-full max-w-[420px]">
        {appSchool ? <AppSchoolHeader /> : <div className="font-extrabold text-brand-600 mb-4">OJP School</div>}
        <h1 className="text-xl font-bold">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
        <div className="mt-5 space-y-3">{children}</div>
        {footer && <div className="mt-5 text-sm text-slate-500 flex flex-wrap gap-x-4 gap-y-1">{footer}</div>}
      </div>
    </main>
  );
}

export const AuthLink = ({ to, children }: { to: string; children: ReactNode }) => (
  <Link to={to} className="text-brand-600 font-semibold">{children}</Link>
);

export const FormError = ({ text }: { text?: string | null }) =>
  text ? <p className="text-sm text-red-600" role="alert">{text}</p> : null;
